import { Box, TextField, Typography, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { EntityUser } from '@services/editorApi.service';

interface UserSearchSelectProps {
  label: string;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searching: boolean;
  searchResults: EntityUser[];
  selectedUsers: { _id: string; name: string }[];
  onAddUser: (user: EntityUser) => void;
  onRemoveUser: (userId: string) => void;
  minHeight?: number;
}

const formLabelSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  marginBottom: '0.35rem',
} as const;

export function UserSearchSelect({
  label,
  searchTerm,
  onSearchChange,
  searching,
  searchResults,
  selectedUsers,
  onAddUser,
  onRemoveUser,
  minHeight = 230,
}: UserSearchSelectProps) {
  return (
    <Box sx={{ minHeight, height: '100%' }}>
      <Typography variant="body2" sx={formLabelSx}>
        {label}
      </Typography>
      <Box sx={{ position: 'relative', mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Type to search & select user"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label={`Search ${label}`}
          aria-describedby={`${label}-search-results`}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '0.25rem',
              '& input': { fontSize: '0.875rem' },
            },
          }}
        />
        {searching && (
          <CircularProgress
            size={16}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Searching"
          />
        )}
        {searchResults.length > 0 && (
          <Box
            id={`${label}-search-results`}
            role="listbox"
            sx={{
              position: 'absolute',
              zIndex: 10,
              width: '100%',
              bgcolor: 'background.paper',
              border: '1px solid lightgray',
              borderRadius: '0.25rem',
              maxHeight: 160,
              overflowY: 'auto',
            }}
          >
            {searchResults.map((user) => (
              <Box
                key={user._id}
                role="option"
                tabIndex={0}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f4f6fc' },
                  '&:focus': { bgcolor: '#f4f6fc', outline: 'none' },
                }}
                onClick={() => onAddUser(user)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onAddUser(user);
                  }
                }}
              >
                <Typography variant="body2">{user.name}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 0.5 }}>
        {selectedUsers.map((user) => (
          <Box
            key={user._id}
            sx={{
              py: 0.3,
              px: 0.25,
              borderBottom: '1px solid #f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ fontSize: '0.875rem' }}>{user.name}</Typography>
            <button
              type="button"
              onClick={() => onRemoveUser(user._id)}
              aria-label={`Remove ${user.name}`}
              title={`Remove ${user.name}`}
              style={{
                border: 0,
                background: 'transparent',
                color: '#6b7280',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              <CloseIcon sx={{ fontSize: '1rem' }} />
            </button>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
